import { useEffect, useMemo, useState } from "react";
import { Box, Button, InputBase, Typography } from "@mui/material";
import DialogWrapper from "components/DialogWrapper";
import { CALOGO } from "assets";

const gold = "#100C74";
/** Background of the section title strip */
const navy = "#D1B174";
const teal = "#0F8B83";
const inkMute = "#20293d";
const border = "#E6E8EC";
const tealTint = "#E6F5F3";
const warnBg = "#FFF8E6";
const warnBd = "#F2E0AE";
const warn = "#8A6100";

const SOURCE_URL =
  "https://tmdicai.org/assets/Doc/Minimum-recommended-scale-of-Fees-Feb-2020.pdf";

type Row = {
  /** Clause marker as printed in the ICAI table, e.g. "(a)", "i)", "A." */
  label?: string;
  particular: string;
  a?: string;
  b?: string;
  c?: string;
  /** Sub-heading row that carries no fee columns */
  head?: boolean;
  /** Full-width explanatory line inside a section */
  note?: boolean;
  /** Indentation depth of the particular */
  depth?: number;
};

type Section = { no: string; title: string; rows: Row[] };

const SEE_NOTE = "See Note 1";
const note1 = (label: string, particular: string, depth = 0): Row => ({
  label,
  particular,
  a: SEE_NOTE,
  b: SEE_NOTE,
  c: SEE_NOTE,
  depth,
});

const sections: Section[] = [
  {
    no: "1",
    title: "Advising on Drafting of Deeds / Agreements",
    rows: [
      { label: "(a) i)", particular: "Partnership Deed", a: "15,000/- & Above", b: "10,000/- & Above", c: "8,000/- & Above" },
      { label: "(a) ii)", particular: "Partnership Deed (With Consultation & Tax Advisory)", a: "20,000/- & Above", b: "15,000/- & Above", c: "10,000/- & Above" },
      { label: "(b)", particular: "Filing of Forms with Registrar of Firms", a: "7,000/- & Above Per Form", b: "5,000/- & Above Per Form", c: "3,000/- & Above" },
      { label: "(c)", particular: "Supplementary / Modification in Partnership Deed", a: "12,000/- & Above", b: "9,000/- & Above", c: "6,000/- & Above" },
      { label: "(d)", particular: "Joint Development Agreements / Joint Venture Agreements", a: "12,000/- & Above (See Note 1)", b: "9,000/- & Above (See Note 1)", c: "6,000/- & Above (See Note 1)" },
      { label: "(e)", particular: "Other Deeds such as Power of Attorney, Will, Gift Deed etc.", a: "5,000/- & Above", b: "4,000/- & Above", c: "3,000/- & Above" },
    ],
  },
  {
    no: "2",
    title: "Income Tax",
    rows: [
      { label: "A.", particular: "Filing of Return of Income", head: true },
      { label: "I)", particular: "For Individuals / HUFs etc.", head: true, depth: 1 },
      { label: "(a)", particular: "Filing of Return of Income with Salary / Other Sources / Share of Profit", a: "8,000/- & Above", b: "6,000/- & Above", c: "4,000/- & Above", depth: 2 },
      { label: "(b)", particular: "Filing of Return of Income with detailed Capital Gain working", head: true, depth: 2 },
      { label: "i)", particular: "Less than 10 Transactions (For Shares & Securities)", a: "11,000/- & Above", b: "8,000/- & Above", c: "5,000/- & Above", depth: 3 },
      { label: "ii)", particular: "More than 10 Transactions (For Shares & Securities)", a: "17,000/- & Above", b: "12,000/- & Above", c: "8,000/- & Above", depth: 3 },
      { label: "(c)", particular: "Filing of Return of Income for Capital Gain on Immovable property", a: "32,000/- & Above", b: "22,000/- & Above", c: "15,000/- & Above", depth: 2 },
      { label: "(d)", particular: "Filing of Return of Income with Preparation of Bank Summary, Capital A/c & Balance Sheet", a: "12,000/- & Above", b: "9,000/- & Above", c: "6,000/- & Above", depth: 2 },
      { label: "II)", particular: "Other Assessees", head: true, depth: 1 },
      { label: "(a)", particular: "Partnership Firms / Sole Proprietor with Advisory Services", a: "15,000/- & Above", b: "10,000/- & Above", c: "8,000/- & Above", depth: 2 },
      { label: "(b)", particular: "Minors I.T. Statement", a: "8,000/- & Above", b: "6,000/- & Above", c: "4,000/- & Above", depth: 2 },
      { label: "(c) i)", particular: "Private Ltd. Company — Active", a: "25,000/- & Above", b: "18,000/- & Above", c: "12,000/- & Above", depth: 2 },
      { label: "(c) ii)", particular: "Private Ltd. Company — Defunct", a: "12,000/- & Above", b: "9,000/- & Above", c: "6,000/- & Above", depth: 2 },
      { label: "(d) i)", particular: "Public Ltd. Company — Active", a: "65,000/- & Above", b: "45,000/- & Above", c: "30,000/- & Above", depth: 2 },
      { label: "(d) ii)", particular: "Public Ltd. Company — Defunct", a: "25,000/- & Above", b: "18,000/- & Above", c: "12,000/- & Above", depth: 2 },
      { label: "B.", particular: "Filing of Forms etc. (Quarterly Fees)", head: true },
      { label: "(a) i)", particular: "Filing of TDS / TCS Return (per Form) — With 5 or less Entries", a: "4,000/- & Above", b: "3,000/- & Above", c: "2,000/- & Above", depth: 1 },
      { label: "(a) ii)", particular: "Filing of TDS / TCS Return (per Form) — With more than 5 entries", a: "9,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above", depth: 1 },
      { label: "(b)", particular: "Filing of Form No. 15-H / 15-G (per Set)", a: "4,000/- & Above", b: "3,000/- & Above", c: "2,000/- & Above", depth: 1 },
      { label: "(c)", particular: "Form No. 49-A / 49-B", a: "4,000/- & Above", b: "3,000/- & Above", c: "2,000/- & Above", depth: 1 },
      { label: "(d)", particular: "Any other Forms filed under the Income Tax Act", a: "4,000/- & Above", b: "3,000/- & Above", c: "2,000/- & Above", depth: 1 },
      { label: "C.", particular: "Certificate", head: true },
      { particular: "Obtaining Certificate from Income Tax Department", a: "14,000/- & Above", b: "10,000/- & Above", c: "7,000/- & Above", depth: 1 },
      { label: "D.", particular: "Filing of Appeals etc.", head: true },
      { label: "(a)", particular: "First Appeal — Preparation of Statement of Facts, Grounds of Appeal, Etc.", a: "32,000/- & Above", b: "22,000/- & Above", c: "15,000/- & Above", depth: 1 },
      { label: "(b)", particular: "Second Appeal (Tribunal)", a: "65,000/- & Above", b: "45,000/- & Above", c: "30,000/- & Above", depth: 1 },
      { label: "E.", particular: "Assessments etc.", head: true },
      note1("(a) i)", "Attending Scrutiny Assessment / Appeal — Corporate", 1),
      { label: "(a) ii)", particular: "Attending Scrutiny Assessment / Appeal — Non Corporate", a: "32,000/- & Above", b: "22,000/- & Above", c: "15,000/- & Above", depth: 1 },
      { label: "(b)", particular: "Attending before Authorities", a: "10,000/- & Above Per Visit", b: "7,000/- & Above Per Visit", c: "5,000/- & Above Per Visit", depth: 1 },
      { label: "(c)", particular: "Attending for Rectifications / Refunds / Appeal effects Etc.", a: "7,000/- & Above Per Visit", b: "5,000/- & Above Per Visit", c: "3,000/- & Above Per Visit", depth: 1 },
      { label: "(d)", particular: "Income Tax Survey", a: "80,000/- & Above", b: "55,000/- & Above", c: "35,000/- & Above", depth: 1 },
      { label: "(e)", particular: "T.D.S. Survey", a: "50,000/- & Above", b: "35,000/- & Above", c: "25,000/- & Above", depth: 1 },
      note1("(f)", "Income Tax Search and Seizure", 1),
      note1("(g)", "Any other Consultancy", 1),
    ],
  },
  {
    no: "3",
    title: "Charitable Trust",
    rows: [
      { label: "(a) i)", particular: "Registration Under Local Act", a: "25,000/- & Above", b: "18,000/- & Above", c: "12,000/- & Above" },
      { label: "(a) ii)", particular: "Societies Registration Act", a: "32,000/- & Above", b: "22,000/- & Above", c: "15,000/- & Above" },
      { label: "(b)", particular: "Registration Under Income Tax Act", a: "25,000/- & Above", b: "18,000/- & Above", c: "12,000/- & Above" },
      { label: "(c)", particular: "Exemption Certificate under section 80G of Income Tax Act", a: "20,000/- & Above", b: "15,000/- & Above", c: "10,000/- & Above" },
      { label: "(d)", particular: "Filing Objection Memo / other Replies", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
      { label: "(e)", particular: "Filing of Change Report", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
      { label: "(f)", particular: "Filing of Annual Budget", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
      { label: "(g)", particular: "Attending before Charity Commissioner including for Attending Objections", a: "8,000/- & Above per visit", b: "6,000/- & Above per visit", c: "4,000/- & Above" },
      { label: "(h) i)", particular: "F.C.R.A. Registration", a: "35,000/- & Above", b: "25,000/- & Above", c: "18,000/- & Above" },
      { label: "(h) ii)", particular: "F.C.R.A. Certification", a: "8,000/- & Above", b: "6,000/- & Above", c: "4,000/- & Above" },
    ],
  },
  {
    no: "4",
    title: "Company Law and LLP Work",
    rows: [
      { label: "(a)", particular: "Filing Application for Name Approval", a: "8,000/- & Above", b: "6,000/- & Above", c: "4,000/- & Above" },
      { label: "(b)", particular: "Incorporation of a Private Limited Company / LLP", a: "35,000/- & Above", b: "25,000/- & Above", c: "18,000/- & Above" },
      { label: "(c)", particular: "Incorporation of a Public Limited Company", a: "65,000/- & Above", b: "45,000/- & Above", c: "30,000/- & Above" },
      { label: "(d)", particular: "Advisory or consultation in drafting MOA, AOA", a: "15,000/- & Above", b: "11,000/- & Above", c: "8,000/- & Above" },
      note1("(e) i)", "Company's / LLP ROC Work, Preparation of Minutes, Statutory Register & Other Secretarial Work"),
      { label: "(e) ii)", particular: "Certification (Per Certificate)", a: "15,000/- & Above", b: "11,000/- & Above", c: "8,000/- & Above" },
      { label: "(f)", particular: "Filing Annual Return Etc.", a: "10,000/- & Above per Form", b: "7,000/- & Above per Form", c: "5,000/- & Above" },
      { label: "(g)", particular: "Filing Other Forms Like F-32, 18, 2 etc.", a: "5,000/- & Above", b: "4,000/- & Above per Form", c: "3,000/- & Above" },
      { label: "(h)", particular: "Increase in Authorised Capital — Filing of F-5, F-23, preparation of Revised Memorandum of Association / Article of Association / LLP Agreement", a: "25,000/- & Above", b: "20,000/- & Above", c: "14,000/- & Above" },
      { label: "(i)", particular: "DPIN / DIN per Application", a: "4,000/- & Above", b: "3,000/- & Above", c: "2,000/- & Above" },
      note1("(j)", "Company Law Consultancy including Petition drafting"),
      note1("(k)", "Company Law representation including LLP before RD and NCLT"),
      note1("(l)", "ROC Representation"),
    ],
  },
  {
    no: "5",
    title: "Audit and Other Assignments",
    rows: [
      { particular: "Rate per day would depend on the complexity of the work and the number of days spent by each person.", note: true },
      { label: "(i)", particular: "Principal", a: "18,000/- & Above per day", b: "12,000/- & Above per day", c: "8,000/- & Above per day" },
      { label: "(ii)", particular: "Qualified Assistants", a: "10,000/- & Above per day", b: "7,000/- & Above per day", c: "5,000/- & Above per day" },
      { label: "(iii)", particular: "Semi Qualified Assistants", a: "5,000/- & Above per day", b: "4,000/- & Above per day", c: "3,000/- & Above per day" },
      { label: "(iv)", particular: "Other Assistants", a: "3,000/- & Above per day", b: "2,000/- & Above per day", c: "1,000/- & Above per day" },
      { particular: "Subject to minimum indicative Fees as under:", note: true },
      { label: "(i)", particular: "Tax Audit", a: "40,000/- & Above", b: "30,000/- & Above", c: "22,000/- & Above" },
      { label: "(ii)", particular: "Company Audit", head: true },
      { label: "(a)", particular: "Small Pvt. Ltd. Co. (Turnover up to ₹2 crore)", a: "50,000/- & Above", b: "35,000/- & Above", c: "25,000/- & Above", depth: 1 },
      { label: "(b)", particular: "Medium Size Pvt. Ltd. Co. / Public Ltd. Co.", a: "80,000/- & Above", b: "55,000/- & Above", c: "35,000/- & Above", depth: 1 },
      note1("(c)", "Large Size Pvt. Ltd. Co. / Public Ltd. Co.", 1),
      { label: "(iv)", particular: "Review of TDS Compliance", a: "25,000/- & Above", b: "18,000/- & Above", c: "12,000/- & Above" },
      note1("(v)", "Transfer Pricing Audit"),
    ],
  },
  {
    no: "6",
    title: "Investigation, Management Services or Special Assignments",
    rows: [
      { particular: "Rate per day would depend on the complexity of the work and the number of days spent by each person.", note: true },
      { label: "(a)", particular: "Principal", a: "35,000/- & Above + per day charge", b: "25,000/- & Above + per day charge", c: "18,000/- & Above + per day charge" },
      { label: "(b)", particular: "Qualified Assistant", a: "18,000/- & Above + per day charge", b: "12,000/- & Above + per day charge", c: "8,000/- & Above + per day charge" },
      { label: "(c)", particular: "Semi Qualified Assistant", a: "10,000/- & Above + per day charge", b: "7,000/- & Above + per day charge", c: "5,000/- & Above + per day charge" },
    ],
  },
  {
    no: "7",
    title: "Certification Work",
    rows: [
      note1("(a)", "Issuing Certificates under the Income Tax Act i.e. u/s 80IA / 80IB / 10A / 10B & other Certificates"),
      { label: "(b)", particular: "Other Certificates For LIC / Passport / Credit Card / Etc.", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
      { label: "(c)", particular: "Other Attestation (True Copy)", a: "3,000/- & Above per form", b: "2,000/- & Above per form", c: "1,000/- & Above" },
      { label: "(d)", particular: "Net worth Certificate for person going abroad", a: "18,000/- & Above", b: "12,000/- & Above", c: "8,000/- & Above" },
    ],
  },
  {
    no: "8",
    title: "RERA",
    rows: [
      { label: "(a)", particular: "Audit of Accounts", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
      { label: "(b)", particular: "Appearance Before Appellate Tribunal of Regulatory Authority or Adjudicating Authority", a: "50,000/- & Above", b: "35,000/- & Above", c: "25,000/- & Above" },
      note1("(c)", "Advisory & Consultation"),
      note1("(d)", "Certification for withdrawal of amount"),
    ],
  },
  {
    no: "9",
    title: "Consultation & Arbitration",
    rows: [
      { particular: "Rate per hour would depend on the complexity of the work and the number of hours spent by each person.", note: true },
      { label: "(a)", particular: "Principal", a: "35,000/- & Above (initial fees) + additional fees @ 8,000/- & Above per hour", b: "25,000/- & Above (initial fees) + additional fees @ 6,000/- & Above per hour", c: "18,000/- & Above (initial fees) + additional fees @ 4,000/- & Above per hour" },
      { label: "(b)", particular: "Qualified Assistant", a: "6,000/- & Above per hour", b: "4,000/- & Above per hour", c: "3,000/- & Above per hour" },
      { label: "(c)", particular: "Semi Qualified Assistant", a: "3,000/- & Above per hour", b: "2,000/- & Above per hour", c: "1,000/- & Above per hour" },
    ],
  },
  {
    no: "10",
    title: "NBFC / RBI Matters",
    rows: [
      note1("(a)", "NBFC Registration with RBI"),
      { label: "(b)", particular: "Other Returns", a: "18,000/- & Above", b: "12,000/- & Above", c: "8,000/- & Above" },
    ],
  },
  {
    no: "11",
    title: "GST",
    rows: [
      { label: "(a)", particular: "Registration", a: "20,000/- & Above", b: "15,000/- & Above", c: "10,000/- & Above" },
      note1("(b)", "Registration with Consultation"),
      note1("(c)", "Tax Advisory & Consultation i.e. about value, taxability, classification, etc."),
      { label: "(d)", particular: "Challan / Returns", a: "15,000/- & Above + (4,000/- Per Month)", b: "10,000/- & Above + (3,000/- Per Month)", c: "8,000/- & Above + (2,000/- Per Month)" },
      { label: "(e)", particular: "Adjudication / Show Cause notice reply", a: "30,000/- & Above", b: "20,000/- & Above", c: "15,000/- & Above" },
      { label: "(f)", particular: "Filing of Appeal / Appeals Drafting", a: "30,000/- & Above", b: "20,000/- & Above", c: "15,000/- & Above" },
      note1("(g)", "Furnish details of inward / outward supply"),
      note1("(h)", "Misc. services i.e. refund, cancellation / revocation registration, maintain electronic cash ledger etc."),
      { label: "(i)", particular: "Audit of accounts and reconciliation Statement", a: "40,000/- & Above", b: "20,000/- & Above", c: "12,000/- & Above" },
      { label: "(j)", particular: "Any Certification Work", a: "10,000/- & Above", b: "7,000/- & Above", c: "5,000/- & Above" },
    ],
  },
  {
    no: "12",
    title: "FEMA Matters",
    rows: [
      { label: "(a)", particular: "Filing Declaration with RBI in relation to transaction by NRIs / OCBs", a: "35,000/- & Above", b: "25,000/- & Above", c: "18,000/- & Above" },
      { label: "(b)", particular: "Obtaining Prior Permissions from RBI for Transaction with NRIs / OCBs", a: "50,000/- & Above", b: "35,000/- & Above", c: "25,000/- & Above" },
      note1("(c)", "Technical collaboration: Advising, obtaining RBI permission, drafting and preparing technical collaboration agreement and incidental matters"),
      note1("(d)", "Foreign collaboration: Advising, obtaining RBI permission, drafting and preparing technical collaboration agreement and incidental matters (incl. Shareholders Agreement)"),
      note1("(e)", "Advising on Non Resident Taxation Matters including Double Tax Avoidance Agreements including FEMA"),
    ],
  },
  {
    no: "13",
    title: "Project Financing",
    rows: [note1("(a)", "Preparation of CMA data"), note1("(b)", "Services relating to Financial sector")],
  },
  {
    no: "14",
    title: "Accountancy Services",
    rows: [
      note1("", "Book Keeping and the preparation of financial statements"),
      note1("", "Other Services"),
    ],
  },
  {
    no: "15",
    title: "Other Services",
    rows: [note1("", "Other Services not listed above")],
  },
];

const notes = [
  "Fees to be charged depending on the complexity and the time spent on the particular assignment.",
  "The above recommended minimum scale of fees is as recommended by the Committee for Members in Practice (CMP) of ICAI.",
  "The aforesaid table states recommendatory minimum scale of fees worked out by taking into account average time required to complete such assignments. However, members are free to charge varying rates depending upon the nature and complexity of assignment and time involved in completing the same.",
  "Office time spent in travelling & out-of-pocket expenses would be chargeable. The Committee issues for general information the above recommended scale of fees which it considers reasonable under present conditions. It will be appreciated that the actual fees charged in individual cases will be a matter of agreement between the member and the client.",
  "GST should be collected separately wherever applicable.",
  "The Committee also recommends that the bill for each service should be raised separately and immediately after the services are rendered.",
  "Classification of Class A, Class B & Class C Cities are given in Annexure A.",
  "The amount charged will be based on the location of the service provider.",
];

/** Annexure A — [State/UT, Class A cities, Class B cities, Class C cities] */
const cityClassification: [string, string, string, string][] = [
  ["Andaman & Nicobar Islands", "—", "—", "All cities"],
  ["Andhra Pradesh", "—", "Vijayawada, Greater Visakhapatnam, Guntur, Nellore", "Other Cities"],
  ["Arunachal Pradesh", "—", "—", "All cities"],
  ["Assam", "—", "Guwahati", "Other Cities"],
  ["Bihar", "—", "Patna", "Other Cities"],
  ["Chandigarh", "—", "Chandigarh", "—"],
  ["Chhattisgarh", "—", "Durg-Bhilai Nagar, Raipur", "Other Cities"],
  ["Dadra & Nagar Haveli", "—", "—", "All cities"],
  ["Daman & Diu", "—", "—", "All cities"],
  ["Delhi", "Delhi", "—", "—"],
  ["Goa", "—", "—", "All cities"],
  ["Gujarat", "Ahmedabad", "Rajkot, Jamnagar, Bhavnagar, Vadodara, Surat", "Other Cities"],
  ["Haryana", "—", "Faridabad, Gurgaon", "Other Cities"],
  ["Himachal Pradesh", "—", "—", "All cities"],
  ["Jammu & Kashmir", "—", "Srinagar, Jammu", "Other Cities"],
  ["Jharkhand", "—", "Jamshedpur, Dhanbad, Ranchi, Bokaro Steel City", "Other Cities"],
  ["Karnataka", "Bengaluru", "Belgaum, Hubli-Dharwad, Mangalore, Mysore, Gulbarga", "Other Cities"],
  ["Kerala", "—", "Kozhikode, Kochi, Thiruvananthapuram, Thrissur, Malappuram, Kannur, Kollam", "Other Cities"],
  ["Lakshadweep", "—", "—", "All cities"],
  ["Madhya Pradesh", "—", "Gwalior, Indore, Bhopal, Jabalpur, Ujjain", "Other Cities"],
  ["Maharashtra", "Greater Mumbai, Pune", "Amravati, Nagpur, Aurangabad, Nashik, Bhiwandi, Solapur, Kolhapur, Vasai-Virar City, Malegaon, Nanded-Waghala, Sangli", "Other Cities"],
  ["Manipur", "—", "—", "All cities"],
  ["Meghalaya", "—", "—", "All cities"],
  ["Mizoram", "—", "—", "All cities"],
  ["Nagaland", "—", "—", "All cities"],
  ["Odisha", "—", "Cuttack, Bhubaneswar, Rourkela", "Other Cities"],
  ["Puducherry", "—", "Puducherry / Pondicherry", "—"],
  ["Punjab", "—", "Amritsar, Jalandhar, Ludhiana", "Other Cities"],
  ["Rajasthan", "—", "Bikaner, Jaipur, Jodhpur, Kota, Ajmer", "Other Cities"],
  ["Sikkim", "—", "—", "All cities"],
  ["Tamil Nadu", "Chennai", "Salem, Tiruppur, Coimbatore, Tiruchirappalli, Madurai, Erode", "Other Cities"],
  ["Telangana", "Hyderabad", "Warangal", "Other Cities"],
  ["Tripura", "—", "—", "All cities"],
  ["Uttar Pradesh", "—", "Moradabad, Meerut, Ghaziabad, Aligarh, Agra, Bareilly, Lucknow, Kanpur, Allahabad, Gorakhpur, Varanasi, Saharanpur, Noida, Firozabad, Jhansi", "Other Cities"],
  ["Uttarakhand", "—", "Dehradun", "Other Cities"],
  ["West Bengal", "Kolkata", "Asansol, Siliguri, Durgapur", "Other Cities"],
];

const gridCols = "minmax(240px, 2.2fr) repeat(3, minmax(120px, 1fr))";
const cellSx = {
  fontSize: 11.5,
  lineHeight: 1.45,
  p: "9px 12px",
  borderBottom: `1px solid ${border}`,
};

/** Prefixes every amount in a fee cell with the rupee sign, e.g. "15,000/-" -> "₹15,000/-" */
const withRupee = (v?: string) => v?.replace(/(\d[\d,]*\/-)/g, "₹$1");

const escapeRegExp = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Renders `text` with every occurrence of `query` marked */
const Highlight = (props: { text: string; query: string }) => {
  const { text, query } = props;
  if (!query) return <>{text}</>;

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <Box
            key={i}
            component="mark"
            sx={{ bgcolor: "#FFF1B8", color: "inherit", px: 0.25, borderRadius: 0.5 }}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </>
  );
};

const FeeRow = (props: { row: Row; query: string }) => {
  const { row, query } = props;
  const pad = 1 + (row.depth || 0) * 1.5;
  const particular = <Highlight text={row.particular} query={query} />;

  if (row.note) {
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          ...cellSx,
          bgcolor: tealTint,
          color: "#0B5F5A",
          fontStyle: "italic",
          fontSize: 11,
        }}
      >
        {particular}
      </Box>
    );
  }

  if (row.head) {
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          ...cellSx,
          bgcolor: "#F4F6F8",
          fontWeight: 700,
          fontSize: 11,
          pl: pad,
        }}
      >
        {row.label ? `${row.label} ` : ""}
        {particular}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ ...cellSx, pl: pad }}>
        {row.label ? (
          <Box component="span" sx={{ color: inkMute, mr: 0.75 }}>
            {row.label}
          </Box>
        ) : null}
        {particular}
      </Box>
      {[row.a, row.b, row.c].map((v, i) => (
        <Box
          key={i}
          sx={{
            ...cellSx,
            textAlign: "center",
            color: v === SEE_NOTE ? inkMute : "#1F2733",
            fontStyle: v === SEE_NOTE ? "italic" : "normal",
            fontWeight: v === SEE_NOTE ? 400 : 600,
          }}
        >
          {withRupee(v)}
        </Box>
      ))}
    </>
  );
};

const ServiceDescriptionAnnouncement = (props: { open: boolean; setOpen: (v: boolean) => void }) => {
  const { open, setOpen } = props;
  const [search, setSearch] = useState("");
  const [showCities, setShowCities] = useState(false);

  /** The dialog stays mounted, so clear the filters each time it is closed */
  useEffect(() => {
    if (!open) {
      setSearch("");
      setShowCities(false);
    }
  }, [open]);

  const query = search.trim();

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => {
        if (s.title.toLowerCase().includes(q)) return s;
        return { ...s, rows: s.rows.filter((r) => r.particular.toLowerCase().includes(q)) };
      })
      .filter((s) => s.rows.length > 0);
  }, [query]);

  /** Annexure A rows matched on state or on any city in the three classes, keeping the printed serial number */
  const filteredCities = useMemo(() => {
    const q = query.toLowerCase();
    const numbered = cityClassification.map((row, i) => ({ row, no: i + 1 }));
    if (!q) return numbered;
    return numbered.filter(({ row }) => row.some((v) => v.toLowerCase().includes(q)));
  }, [query]);

  return (
    <DialogWrapper
      title="ICAI Minimum Recommended Scale of Fees"
      open={open}
      setOpen={setOpen}
      width="lg"
    >
      {/* Banner */}
      <Box
        sx={{
          bgcolor: navy,
          p: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 2,
        }}
      >
        <Box
          
        >
          <img src= {CALOGO} alt="CA Logo" width={60} height={60} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1F3864" }}>
            Revised Minimum Recommended Scale of Fees
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#2B2013", mt: 0.5, lineHeight: 1.5 }}>
            The Committee for Members in Practice (CMP) of ICAI as a part of its commitment to strengthen the Practitioners
has initiated the Revised Minimum Recommended Scale of Fees for the professional assignments done by the
members of ICAI. The recommendation is about the fee to be charged as per the work performed for various
professional assignments. The fee has been recommended separately for Class A, Class B and Class C cities.
          </Typography>
        </Box>
        <Box
          sx={{
            textAlign: "center",
            bgcolor: "rgba(255,255,255,0.1)",
            p: "10px 18px",
            borderRadius: 2.5,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#2B4C9B", lineHeight: 1 }}>15</Typography>
          <Typography
            sx={{
              fontSize: 9,
              color: "#2B4C9B",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mt: 0.25,
            }}
          >
            Service heads
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <Box display="flex" alignItems="center" gap={1.5} mt={2} mb={1.5}>
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            showCities
              ? "Search a state or city — e.g. Pune, Kerala, Noida…"
              : "Search a service — e.g. tax audit, GST, ROC, appeal…"
          }
          sx={{
            flex: 1,
            fontSize: 12,
            border: `1px solid ${border}`,
            borderRadius: 2,
            p: "7px 12px",
            bgcolor: "#FAFBFC",
          }}
        />
        <Button
          size="small"
          variant={showCities ? "contained" : "outlined"}
          onClick={() => setShowCities((v) => !v)}
          sx={{
            textTransform: "none",
            fontSize: 11,
            fontWeight: 600,
            boxShadow: "none",
            whiteSpace: "nowrap",
            ...(showCities
              ? { bgcolor: teal, "&:hover": { bgcolor: "#0C6F69" } }
              : { color: teal, borderColor: teal }),
          }}
        >
          {showCities ? "Minimum Recommended Scale of Fees" : "Annexure A — City classification"}
        </Button>
      </Box>

      {/* Fee tables */}
      <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 0.5 }}>
        {showCities ? (
          filteredCities.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: inkMute, textAlign: "center", py: 4 }}>
              No state or city matches &ldquo;{query}&rdquo;.
            </Typography>
          ) : (
            <Box sx={{ border: `1px solid ${border}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Box
                display="grid"
                gridTemplateColumns="minmax(150px, 1fr) minmax(110px, 0.9fr) minmax(240px, 2.4fr) minmax(110px, 0.9fr)"
                sx={{ minWidth: 640 }}
              >
                {["States / Union Territories", "Class A", "Class B", "Class C"].map((h) => (
                  <Box
                    key={h}
                    sx={{
                      ...cellSx,
                      bgcolor: navy,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 10.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {h}
                  </Box>
                ))}
                {filteredCities.map(({ row: [state, a, b, c], no }, i) => (
                  <Box key={state} display="contents">
                    <Box sx={{ ...cellSx, fontWeight: 600, bgcolor: i % 2 ? "#FAFBFC" : "#fff" }}>
                      {no}. <Highlight text={state} query={query} />
                    </Box>
                    {[a, b, c].map((v, j) => (
                      <Box
                        key={j}
                        sx={{
                          ...cellSx,
                          bgcolor: i % 2 ? "#FAFBFC" : "#fff",
                          color: v === "—" ? inkMute : "#1F2733",
                        }}
                      >
                        <Highlight text={v} query={query} />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          )
        ) : (
          <>
            {filtered.map((s) => (
              <Box
                key={s.no}
                sx={{ border: `1px solid ${border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.75 }}
              >
                <Box
                  sx={{
                    bgcolor: gold,
                    borderBottom: `1px solid ${border}`,
                    p: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: tealTint,
                      color: teal,
                      fontSize: 10.5,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {s.no}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: navy,
                    }}
                  >
                    <Highlight text={s.title} query={query} />
                  </Typography>
                </Box>

                <Box sx={{ overflowX: "auto" }}>
                  <Box display="grid" gridTemplateColumns={gridCols} sx={{ minWidth: 620 }}>
                    {["Particulars", "Class A Cities", "Class B Cities", "Class C Cities"].map(
                      (h, i) => (
                        <Box
                          key={h}
                          sx={{
                            ...cellSx,
                            bgcolor: navy,
                            color: "#000",
                            fontWeight: 700,
                            fontSize: 12,
                            textTransform: "none",
                            letterSpacing: 0.4,
                            textAlign: i === 0 ? "left" : "center",
                          }}
                        >
                          {h}
                        </Box>
                      )
                    )}
                    {s.rows.map((r, i) => (
                      <FeeRow key={`${s.no}-${i}`} row={r} query={query} />
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}

            {filtered.length === 0 && (
              <Typography sx={{ fontSize: 12, color: inkMute, textAlign: "center", py: 4 }}>
                No service matches &ldquo;{query}&rdquo;.
              </Typography>
            )}

            {/* Notes */}
            {!search && (
              <Box sx={{ bgcolor: warnBg, border: `1px solid ${warnBd}`, borderRadius: 2.5, p: "15px 17px" }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: warn, mb: 1 }}>
                  Notes
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.25, fontSize: 11.5, lineHeight: 1.75 }}>
                  {notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 2,
          pt: 1.75,
          borderTop: `1px solid ${border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 10.5, color: inkMute }}>
          Source:{" "}
          <Box
            component="a"
            href={SOURCE_URL}
            target="_blank"
            rel="noreferrer"
            sx={{ color: teal, fontWeight: 600 }}
          >
            Minimum Recommended Scale of Fees, Feb 2020 (ICAI)
          </Box>{" "}
          &middot; Recommendatory only &mdash; actual fees are a matter of agreement between the member and
          the client.
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setOpen(false)}
          sx={{
            bgcolor: teal,
            "&:hover": { bgcolor: "#0C6F69" },
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          Close
        </Button>
      </Box>
    </DialogWrapper>
  );
};

export default ServiceDescriptionAnnouncement;
